import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'

export interface SliderOptions {
  slidesPerView?: number | 'auto'
  slidesPerGroup?: number
  spaceBetween?: number
  loop?: boolean
  autoplay?: false | { delay: number; pauseOnHover?: boolean }
  speed?: number
  lazy?: boolean
  navigation?: {
    prevEl?: HTMLElement | string | null
    nextEl?: HTMLElement | string | null
  }
  pagination?: {
    el: HTMLElement | string | null
    type?: 'bullets' | 'custom'
    clickable?: boolean
    renderCustom?: (current: number, total: number) => string
  }
  on?: {
    init?: (slider: Slider) => void
    slideChange?: (slider: Slider) => void
    reachEnd?: (slider: Slider) => void
    reachStart?: (slider: Slider) => void
    resize?: (slider: Slider) => void
    click?: (slider: Slider, event: MouseEvent) => void
  }
}

const defaultOptions: SliderOptions = {
  slidesPerView: 'auto',
  slidesPerGroup: 1,
  spaceBetween: 16,
  loop: false,
  autoplay: false,
  speed: 300,
  lazy: true,
}

export default (element: HTMLElement | string, options: SliderOptions = {}): Slider | undefined => {
  const el = typeof element === 'string' ? document.querySelector<HTMLElement>(element) : element

  if (!el) {
    return undefined
  }

  let slider: Slider | undefined

  try {
    slider = new Slider(el, options)
  } catch (error) {
    console.error('Slider: Failed to initialize', error)
  }

  return slider
}

export class Slider {
  private element: HTMLElement
  private wrapper: HTMLElement
  private options: SliderOptions
  private slides: HTMLElement[] = []
  private prevButton: HTMLElement | null = null
  private nextButton: HTMLElement | null = null
  private paginationEl: HTMLElement | null = null
  private autoplayInterval: ReturnType<typeof setInterval> | null = null
  private resizeObserver: ResizeObserver | null = null
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null
  private activeIndex = 0
  private slideWidth = 0
  private initialized = false
  private isScrolling = false
  private slideScrollTimeout: ReturnType<typeof setTimeout> | null = null

  constructor(element: HTMLElement, options: SliderOptions = {}) {
    this.element = element
    const wrapper = element.querySelector<HTMLElement>('.slider-wrapper')

    if (!wrapper) {
      throw new Error('Slider: Wrapper element (.slider-wrapper) not found')
    }

    this.wrapper = wrapper
    this.options = { ...defaultOptions, ...options }

    if (this.options.lazy && 'IntersectionObserver' in window) {
      const sliderObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            sliderObserver.unobserve(entry.target)
            this.init()
          }
        })
      })

      sliderObserver.observe(this.element)
    } else {
      this.init()
    }
  }

  private init(): void {
    this.setSlides()
    this.setStyles()
    this.initNavigation()
    this.initPagination()
    this.initScrollListener()
    this.initResizeObserver()

    if (this.options.loop) {
      this.initLoop()
    }

    if (this.options.autoplay) {
      this.initAutoplay()
    }

    this.initA11y()
    this.initClickListener()

    this.element.classList.add('slider-initialized')
    this.initialized = true
    this.updateNavigation()
    this.updatePagination()
    this.updateActiveSlide()

    ElementPropertiesManager.addProperty(this.element, 'slider', this)

    this.options.on?.init?.(this)
  }

  private setSlides(): void {
    this.slides = Array.from(this.wrapper.querySelectorAll<HTMLElement>(':scope > .slide'))
  }

  private setStyles(): void {
    const { slidesPerView, spaceBetween } = this.options

    if (typeof slidesPerView === 'number') {
      this.element.style.setProperty('--slider-slides-per-view', slidesPerView.toString())
    }

    if (spaceBetween !== undefined) {
      this.element.style.setProperty('--slider-gap', `${spaceBetween}px`)
    }

    this.calculateSlideWidth()
  }

  private calculateSlideWidth(): void {
    const firstSlide = this.slides[0]
    if (firstSlide) {
      const gap = this.options.spaceBetween ?? 0
      this.slideWidth = firstSlide.offsetWidth + gap
    }
  }

  private initLoop(): void {
    requestAnimationFrame(() => {
      this.slideTo(0, true)
    })
  }

  private initNavigation(): void {
    const { navigation } = this.options
    if (!navigation) {
      return
    }

    this.prevButton = this.resolveElement(navigation.prevEl)
    this.nextButton = this.resolveElement(navigation.nextEl)

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev())
      this.prevButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.prev()
        }
      })
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next())
      this.nextButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.next()
        }
      })
    }
  }

  private initPagination(): void {
    const { pagination } = this.options
    if (!pagination) return

    this.paginationEl = this.resolveElement(pagination.el)
    if (!this.paginationEl) return

    if (pagination.type !== 'custom') {
      this.renderBullets()
    }
  }

  private renderBullets(): void {
    if (!this.paginationEl) return

    this.paginationEl.innerHTML = ''
    this.paginationEl.classList.add('slider-pagination-bullets')

    const groupSize = this.options.slidesPerGroup || 1
    const totalPages = Math.ceil(this.slides.length / groupSize)

    for (let i = 0; i < totalPages; i++) {
      const bullet = document.createElement('button')
      bullet.className = 'slider-bullet'
      bullet.setAttribute('type', 'button')
      bullet.setAttribute('aria-label', `Go to page ${i + 1}`)

      const activePage = Math.floor(this.activeIndex / groupSize)
      if (i === activePage) {
        bullet.setAttribute('aria-current', 'true')
      }

      if (this.options.pagination?.clickable !== false) {
        bullet.addEventListener('click', () => this.slideTo(i * groupSize))
      }

      this.paginationEl?.appendChild(bullet)
    }
  }

  private updatePagination(): void {
    const { pagination } = this.options
    if (!pagination || !this.paginationEl) return

    const groupSize = this.options.slidesPerGroup || 1
    const activePage = Math.floor(this.activeIndex / groupSize)
    const totalPages = Math.ceil(this.slides.length / groupSize)

    if (pagination.type !== 'custom') {
      const bullets = this.paginationEl.querySelectorAll('.slider-bullet')
      bullets.forEach((bullet, index) => {
        if (index === activePage) {
          bullet.setAttribute('aria-current', 'true')
        } else {
          bullet.removeAttribute('aria-current')
        }
      })
    } else if (pagination.renderCustom) {
      this.paginationEl.innerHTML = pagination.renderCustom(activePage + 1, totalPages)
    }
  }

  private initScrollListener(): void {
    const handleScroll = (): void => {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }
      this.updateActiveIndexFromScroll()
    }

    this.wrapper.addEventListener('scroll', handleScroll, { passive: true })
  }

  private updateActiveIndexFromScroll(): void {
    if (this.slideWidth <= 0) {
      return
    }

    const scrollLeft = this.wrapper.scrollLeft

    let newIndex = Math.round(scrollLeft / this.slideWidth)
    newIndex = Math.max(0, Math.min(newIndex, this.slides.length - 1))

    if (this.isScrolling || newIndex === this.activeIndex) return

    this.activeIndex = newIndex
    this.updateActiveSlide()
    this.updateNavigation()
    this.updatePagination()
    this.options.on?.slideChange?.(this)

    if (newIndex === 0) {
      this.options.on?.reachStart?.(this)
    }
    if (newIndex === this.slides.length - 1) {
      this.options.on?.reachEnd?.(this)
    }
  }

  private initResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateSlideWidth()
      this.slideTo(this.activeIndex, true)
      this.options.on?.resize?.(this)
    })

    this.resizeObserver.observe(this.element)
  }

  private initAutoplay(): void {
    const autoplay = this.options.autoplay
    if (!autoplay) return

    this.startAutoplay()

    if (autoplay.pauseOnHover) {
      this.element.addEventListener('mouseenter', () => this.pauseAutoplay())
      this.element.addEventListener('mouseleave', () => this.startAutoplay())
      this.element.addEventListener('touchstart', () => this.pauseAutoplay(), { passive: true })
      this.element.addEventListener('touchend', () => this.startAutoplay())
    }
  }

  private startAutoplay(): void {
    const autoplay = this.options.autoplay
    if (!autoplay || this.autoplayInterval) return

    this.autoplayInterval = setInterval(() => {
      this.next()
    }, autoplay.delay)
  }

  private pauseAutoplay(): void {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval)
      this.autoplayInterval = null
    }
  }

  private initClickListener(): void {
    if (!this.options.on?.click) return

    this.wrapper.addEventListener('click', (event: MouseEvent) => {
      this.options.on?.click?.(this, event)
    })
  }

  private initA11y(): void {
    this.element.setAttribute('role', 'region')
    this.element.setAttribute('aria-roledescription', 'carousel')
    this.wrapper.setAttribute('role', 'group')
    this.wrapper.setAttribute('aria-live', 'polite')

    this.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group')
      slide.setAttribute('aria-roledescription', 'slide')
      slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`)
    })
  }

  private updateNavigation(): void {
    if (!this.options.loop) {
      const isStart = this.activeIndex === 0
      const groupSize = this.options.slidesPerGroup || 1
      const isEnd = this.activeIndex >= this.slides.length - groupSize

      if (this.prevButton) {
        this.prevButton.setAttribute('aria-disabled', isStart.toString())
        if (isStart) {
          this.prevButton.classList.add('slider-button-disabled')
        } else {
          this.prevButton.classList.remove('slider-button-disabled')
        }
      }

      if (this.nextButton) {
        this.nextButton.setAttribute('aria-disabled', isEnd.toString())
        if (isEnd) {
          this.nextButton.classList.add('slider-button-disabled')
        } else {
          this.nextButton.classList.remove('slider-button-disabled')
        }
      }
    }
  }

  private updateActiveSlide(): void {
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('slide-active', index === this.activeIndex)
      slide.classList.toggle('slide-prev', index === this.activeIndex - 1)
      slide.classList.toggle('slide-next', index === this.activeIndex + 1)
    })
  }

  private resolveElement(el: HTMLElement | string | null | undefined): HTMLElement | null {
    if (!el) return null
    if (typeof el === 'string') {
      return this.element.querySelector<HTMLElement>(el) || document.querySelector<HTMLElement>(el)
    }
    return el
  }

  slideTo(index: number, instant = false): void {
    let targetIndex = index
    const step = this.options.slidesPerGroup || 1

    if (this.options.loop) {
      if (index < 0) {
        const totalPages = Math.ceil(this.slides.length / step)
        targetIndex = (totalPages - 1) * step
      } else if (index >= this.slides.length) {
        targetIndex = 0
      }
    } else {
      targetIndex = Math.max(0, Math.min(index, this.slides.length - 1))
    }

    this.activeIndex = targetIndex

    const targetScroll = targetIndex * this.slideWidth

    if (instant) {
      this.updateActiveSlide()
      this.wrapper.style.scrollBehavior = 'auto'
      this.wrapper.scrollTo({ left: targetScroll, behavior: 'auto' })

      requestAnimationFrame(() => {
        this.wrapper.style.scrollBehavior = ''
      })
    } else {
      if (this.slideScrollTimeout) {
        clearTimeout(this.slideScrollTimeout)
      }
      this.isScrolling = true
      this.wrapper.scrollTo({ left: targetScroll, behavior: 'smooth' })

      this.slideScrollTimeout = setTimeout(() => {
        this.isScrolling = false
        this.slideScrollTimeout = null
        this.updateActiveSlide()
      }, this.options.speed || 300)
    }

    this.updateNavigation()
    this.updatePagination()
    this.options.on?.slideChange?.(this)
  }

  next(): void {
    const step = this.options.slidesPerGroup || 1
    if (this.options.loop) {
      this.slideTo(this.activeIndex + step)
    } else if (this.activeIndex < this.slides.length - 1) {
      this.slideTo(this.activeIndex + step)
    }
  }

  prev(): void {
    const step = this.options.slidesPerGroup || 1
    if (this.options.loop) {
      this.slideTo(this.activeIndex - step)
    } else if (this.activeIndex > 0) {
      this.slideTo(this.activeIndex - step)
    }
  }

  update(newConfig?: SliderOptions): void {
    if (newConfig) {
      this.options = { ...this.options, ...newConfig }
    }

    this.setSlides()
    this.slideWidth = 0

    requestAnimationFrame(() => {
      if (this.options.loop) {
        this.initLoop()
      }

      this.calculateSlideWidth()

      if (this.options.pagination?.type !== 'custom') {
        this.renderBullets()
      }

      this.updatePagination()
      this.updateNavigation()
      this.updateActiveSlide()
    })
  }

  getActiveIndex(): number {
    return this.activeIndex
  }

  getSlides(): HTMLElement[] {
    return this.slides
  }

  destroy(): void {
    this.pauseAutoplay()
    this.resizeObserver?.disconnect()

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }

    this.element.classList.remove('slider-initialized')
    this.slides = []
    this.initialized = false
  }
}
